import { createAsyncThunk } from '@reduxjs/toolkit'
// eslint-disable-next-line import/no-cycle
import {
  forceLogout,
  getNotificationMessage,
  setIsUpdatingAvatar,
  setMarkerUpdate,
  addCodeWordsOnSearchBar,
  removeEventMarker
} from 'store'
import L from 'leaflet'
import { REJECTED } from 'utils/constants'
import { Buffer } from 'buffer'
import { markersApi } from './api'

// For separate image requests
export const getMarkerAvatars = createAsyncThunk(
  'markers/getAvatars',
  async (body, { rejectWithValue }) => {
    const {
      isAuth, type, id
    } = body
    try {
      const response = await markersApi.getMarkerAvatars({ isAuth, type, id })

      let data = {
        markerID: id,
        imageBase64: 'data:image/png;base64,' + Buffer.from(response.data, 'binary').toString('base64')
      }

      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// for grouped marker avatar request
export const updateMarkerAvatars = createAsyncThunk(
  'markers/updateMarkerAvatars',
  async (body, { rejectWithValue, dispatch }) => {
    const { isAuth, markerId } = body
    try {
      const response = await markersApi.updateMarkerAvatars({ isAuth, markerid: markerId })
      dispatch(setIsUpdatingAvatar(false))

      return response.data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const getMarkers = createAsyncThunk(
  'markers/getMarkers',
  async (body, { rejectWithValue, dispatch }) => {
    const {
      filter = [],
      isAuth
    } = body

    let northEast = L.latLng(JSON.parse(localStorage.getItem('northeast')))
    let southWest = L.latLng(JSON.parse(localStorage.getItem('southwest')))
    let markerId = []

    try {
      const response = await markersApi.getMarkers({ filter, isAuth })

      let stringCoordCount = 0
      response.data.data.forEach((item) => {
        const duplicatedLNGofNE = northEast?.lng > 0
          ? northEast?.lng - 360
          : northEast?.lng + 360

        const duplicatedLNGofSW = southWest?.lng > 0
          ? southWest?.lng - 360
          : southWest?.lng + 360

        if (
          isAuth
          && item?.is_friends === 'accepted'
          && (
            (
              item.coordinates[0] <= northEast.lat
              && item.coordinates[0] >= southWest.lat
              && item.coordinates[1] <= northEast.lng
              && item.coordinates[1] >= southWest.lng
            )
            || (
              item.coordinates[0] <= northEast.lat
              && item.coordinates[0] >= southWest.lat
              && item.coordinates[1] <= duplicatedLNGofNE
              && item.coordinates[1] >= duplicatedLNGofSW
            )
          )
        ) {
          markerId.push(item?.id)
        }

        if (typeof item.coordinates[0] === 'string' || typeof item.coordinates[1] === 'string') {
          stringCoordCount += 1
        }
      })

      if (stringCoordCount > 0) {
        dispatch(getNotificationMessage({
          type: REJECTED,
          message: 'One of the marker coordinates value has wrong format.',
          isEditProfile: false
        }))
      }

      if (isAuth && markerId.length > 0 && sessionStorage.getItem('id') === null) {
        await dispatch(updateMarkerAvatars({ isAuth: true, markerId }))
      }

      await dispatch(setMarkerUpdate(true))
      return response.data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// For getting friends markers
export const getFriendMarkers = createAsyncThunk(
  'markers/getFriendMarkers',
  async (body, { rejectWithValue, dispatch }) => {
    const { isAuth } = body
    try {
      const response = await markersApi.getFriendMarkers({ isAuth })
      await dispatch(setMarkerUpdate(true))
      return response.data.data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Live Marker Update
export const liveMarkerUpdate = createAsyncThunk(
  'markers/liveMarkerUpdate',
  async (body, { rejectWithValue, dispatch }) => {
    const { isAuth, northeast, southwest } = body

    try {
      const response = await markersApi.liveMarkerUpdate({ isAuth, northeast, southwest })

      await dispatch(setMarkerUpdate(true))
      return response.data
    } catch (error) {
      await dispatch(forceLogout(error.data.message))

      return rejectWithValue(error.message)
    }
  }
)

// Static Marker Update
export const staticMarkerUpdate = createAsyncThunk(
  'markers/staticMarkerUpdate',
  async (body, { rejectWithValue, dispatch }) => {
    const { isAuth, northeast, southwest } = body
    try {
      const response = await markersApi.staticMarkerUpdate({ isAuth, northeast, southwest })

      await dispatch(setMarkerUpdate(true))
      return response.data
    } catch (error) {
      await dispatch(forceLogout(error.data.message))

      return rejectWithValue(error.message)
    }
  }
)

// Single Marker Update
export const singleMarkerUpdate = createAsyncThunk(
  'markers/singleMarkerUpdate',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await markersApi.singleMarkerUpdate({ id })

      dispatch(addCodeWordsOnSearchBar(response.data.data[0].codewords))

      await dispatch(setMarkerUpdate(true))
      return response.data
    } catch (error) {
      await dispatch(forceLogout(error.data.message))

      return rejectWithValue(error.message)
    }
  }
)

// Get Event Marker
export const getEventMarkers = createAsyncThunk(
  'markers/eventMarkers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await markersApi.getEventMarkers()

      return response.data.events
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

const removeEventMarkerAndPopup = (dispatch, id) => {
  const markerPopupOverlay = document.querySelector('#custom-popup-overlay-event')
  if (markerPopupOverlay) {
    markerPopupOverlay.click()
  }

  setTimeout(() => {
    dispatch(removeEventMarker(id))
    dispatch(getEventMarkers())
  }, markerPopupOverlay ? 800 : 0)
}

// Get Event Data
export const getEventData = createAsyncThunk(
  'markers/eventData',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await markersApi.getEventData({ id })

      if (response.data.events.length === 0) {
        return removeEventMarkerAndPopup(dispatch, id)
      }

      return response.data.events[0]
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Get markers that has the certain codeword
export const getMarkersWithCW = createAsyncThunk(
  'markers/getMarkersWithCW',
  async (title, { rejectWithValue, dispatch }) => {
    try {
      const response = await markersApi.getMarkersWithCW(title)

      return response.data
    } catch (error) {
      await dispatch(forceLogout(error.data.message))

      return rejectWithValue(error.message)
    }
  }
)
